/**
 * Hook for streaming chat via SSE (Server-Sent Events).
 *
 * Uses fetch() with ReadableStream (not EventSource) because we need
 * POST with auth headers. Parses SSE events manually from the stream.
 */

import { useState, useCallback, useRef } from 'react';
import { getAccessToken } from '../services/api';
import type { ChatContext } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

/** SSE event types emitted by the streaming endpoint. */
type StreamStatus = 'idle' | 'connecting' | 'streaming' | 'done' | 'error';

interface StreamMetadata {
  confidence: number;
  tokens_used: number;
  grounding_score: number;
}

interface StreamCitation {
  index: number;
  source: {
    source_id: string;
    source_name: string;
    tier: number;
    url?: string;
  };
  relevance: number;
}

interface ChatStreamRequest {
  message: string;
  context: ChatContext;
  include_sources?: boolean;
  max_tokens?: number;
  conversation_history?: Array<{ role: 'user' | 'assistant'; content: string }>;
}

interface UseChatStreamReturn {
  /** Current accumulated response text */
  content: string;
  /** Citations received so far */
  citations: StreamCitation[];
  /** Metadata from the done event */
  metadata: StreamMetadata | null;
  /** Current streaming status */
  status: StreamStatus;
  /** Error message if status is 'error' */
  error: string | null;
  /** Whether we are actively receiving tokens */
  isStreaming: boolean;
  /** Send a message and start streaming */
  sendMessage: (request: ChatStreamRequest) => Promise<void>;
  /** Reset state to idle */
  reset: () => void;
}

/**
 * Parse SSE events from a text chunk.
 *
 * SSE format:
 *   event: <type>\n
 *   data: <json>\n
 *   \n
 */
function parseSSEEvents(text: string): Array<{ event: string; data: string }> {
  const events: Array<{ event: string; data: string }> = [];
  const blocks = text.split('\n\n');

  for (const block of blocks) {
    if (!block.trim()) continue;

    let eventType = '';
    let data = '';

    for (const line of block.split('\n')) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        data = line.slice(6);
      }
    }

    if (eventType && data) {
      events.push({ event: eventType, data });
    }
  }

  return events;
}

export function useChatStream(): UseChatStreamReturn {
  const [content, setContent] = useState('');
  const [citations, setCitations] = useState<StreamCitation[]>([]);
  const [metadata, setMetadata] = useState<StreamMetadata | null>(null);
  const [status, setStatus] = useState<StreamStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  // Ref to track abort controller for cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    // Abort any in-flight request
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;

    setContent('');
    setCitations([]);
    setMetadata(null);
    setStatus('idle');
    setError(null);
  }, []);

  const sendMessage = useCallback(async (request: ChatStreamRequest) => {
    // Reset state for new message
    setContent('');
    setCitations([]);
    setMetadata(null);
    setError(null);
    setStatus('connecting');

    // Create abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const token = getAccessToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/chat/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
        signal: abortController.signal,
      });

      if (!response.ok) {
        setStatus('error');
        setError(`Request failed: ${response.status} ${response.statusText}`);
        return;
      }

      if (!response.body) {
        setStatus('error');
        setError('No response body received');
        return;
      }

      setStatus('streaming');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events in the buffer
        const events = parseSSEEvents(buffer);

        // Keep any incomplete event at the end of the buffer
        const lastDoubleNewline = buffer.lastIndexOf('\n\n');
        if (lastDoubleNewline !== -1) {
          buffer = buffer.slice(lastDoubleNewline + 2);
        }

        for (const event of events) {
          try {
            const parsed = JSON.parse(event.data);

            switch (event.event) {
              case 'token':
                setContent((prev) => prev + (parsed.text || ''));
                break;

              case 'citation':
                setCitations((prev) => [...prev, parsed as StreamCitation]);
                break;

              case 'done':
                setMetadata(parsed as StreamMetadata);
                setStatus('done');
                break;

              case 'error':
                setError(parsed.message || 'Unknown streaming error');
                setStatus('error');
                break;
            }
          } catch {
            // Skip malformed JSON in SSE data
          }
        }
      }

      // If we finished reading without a done event, mark as done
      setStatus((prev) => (prev === 'streaming' ? 'done' : prev));
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        // Request was intentionally aborted, don't set error
        return;
      }
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  }, []);

  return {
    content,
    citations,
    metadata,
    status,
    error,
    isStreaming: status === 'streaming' || status === 'connecting',
    sendMessage,
    reset,
  };
}
